// Where does this pallet or bin stand? — the lookup behind /coldroom's
// "Find a pallet or bin" field and its scanner (PM-CST-04, PB-11).
//
// The page had no search and no scan: the grid shows counts, the drawer shows
// ids, so finding one pallet meant tapping cells one by one, or leaving for
// Home → search → pallet detail. The page only loads the room on screen, so
// this asks the server — the pallet or bin may be in another room.
//
// What a scan or a typed value may be, tried in this order:
//   · a pallet id — compared as a case-folded literal (never normalised: the
//     prefix and dashes are what make it one — see labels/verifyScan.ts);
//   · an SSCC — normalised (a scanner adds the ']C1' AIM prefix and the AI; an
//     operator types the printed "(00)…"), and only when it IS a valid one;
//   · a storage client's own SSCC (pallets.sscc, a separate column) — every
//     candidate of ssccMatchCandidates, NOT gated on validity (their codes);
//   · a bin number — case-folded literal.
import { normaliseSscc, isValidSscc } from '@/lib/sscc'
import { ssccMatchCandidates } from '@/lib/ssccLookup'
import { OVERFLOW_LABEL } from '@/lib/coldroomPlacement'

export type FoundWhat = 'pallet' | 'bin'

export type ColdroomHit =
  | { kind: 'cell';      what: FoundWhat; label: string; roomId: string; cell: string }
  | { kind: 'overflow';  what: FoundWhat; label: string; roomId: string }
  // In a room (IN_COLDROOM) but on no cell and not in the overflow (U18 D2).
  | { kind: 'nocell';    what: FoundWhat; label: string; roomId: string }
  // `where` (pallets): the pallet page's own words for the place it stands —
  // "Loading bay · lane L1 · on M-…" (U18 UF-03). Null when it has none to name.
  | { kind: 'elsewhere'; what: FoundWhat; label: string; status: string; where?: string | null }
  // A built pallet still IN_PRODUCTION: it can be placed from this page (F05 UF-01).
  | { kind: 'awaiting';  what: 'pallet'; label: string; pallet: AwaitingPallet }
  | { kind: 'none';      text: string }
  | { kind: 'error';     text: string }

/** What the Place sheet needs of a pallet found in production. */
export interface AwaitingPallet {
  pallet_id:       string
  version:         number
  tenant_id:       string | null
  awaiting_weight: boolean
  /** run_kind of its run: an EXTERNAL_RECEIPT pallet skips the weigh rule. */
  run_kind:        string | null
  variety:         string | null
}

export interface PlacedRow {
  label:             string
  status:            string
  coldroom_id:       string | null
  coldroom_grid:     string | null
  coldroom_overflow: boolean | null
  // Pallet reads only (bins carry none of these).
  version?:          number
  tenant_id?:        string | null
  awaiting_weight?:  boolean | null
  runs?:             { run_kind: string | null; variety: string | null } | { run_kind: string | null; variety: string | null }[] | null
  // Pallet reads only: the lane it stands in and the truck it is loaded on.
  loading_bay_lane?: string | null
  mission?:          { mission_number: string | null } | { mission_number: string | null }[] | null
}

/** The awaiting-weight hit for a pallet still on its run. */
function awaitingHit(r: PlacedRow & { version: number }): ColdroomHit {
  const run = Array.isArray(r.runs) ? r.runs[0] : r.runs
  return {
    kind: 'awaiting', what: 'pallet', label: r.label,
    pallet: {
      pallet_id: r.label, version: r.version, tenant_id: r.tenant_id ?? null,
      awaiting_weight: !!r.awaiting_weight,
      run_kind: run?.run_kind ?? null, variety: run?.variety ?? null,
    },
  }
}

/** The "it is elsewhere" hit for a pallet that left the room: names the lane and truck. */
function palletElsewhereHit(r: PlacedRow): ColdroomHit {
  const m = Array.isArray(r.mission) ? r.mission[0] : r.mission
  const where = palletWhereabouts(
    { status: r.status, coldroom_id: r.coldroom_id, coldroom_grid: r.coldroom_grid, coldroom_overflow: r.coldroom_overflow, loading_bay_lane: r.loading_bay_lane ?? null },
    { onTruck: true, missionNumber: m?.mission_number ?? null },
  )
  return { kind: 'elsewhere', what: 'pallet', label: r.label, status: r.status, where }
}

/** Where a found row stands, as the page can act on it. */
export function placementOf(what: FoundWhat, r: PlacedRow): ColdroomHit {
  const inRoom = r.status === 'IN_COLDROOM' && !!r.coldroom_id
  if (inRoom && r.coldroom_grid) return { kind: 'cell', what, label: r.label, roomId: r.coldroom_id!, cell: r.coldroom_grid.toUpperCase() }
  if (inRoom && r.coldroom_overflow) return { kind: 'overflow', what, label: r.label, roomId: r.coldroom_id! }
  if (inRoom) return { kind: 'nocell', what, label: r.label, roomId: r.coldroom_id! }
  if (what === 'pallet' && r.status === 'IN_PRODUCTION' && typeof r.version === 'number') return awaitingHit({ ...r, version: r.version })
  if (what === 'pallet' && r.status !== 'IN_COLDROOM') return palletElsewhereHit(r)
  return { kind: 'elsewhere', what, label: r.label, status: r.status }
}

/** The location columns a pallet row carries — all on `pallets` itself. */
export interface WhereaboutsRow {
  status:            string
  coldroom_id?:      string | null
  coldroom_grid:     string | null
  coldroom_overflow: boolean | null
  loading_bay_lane:  string | null
}

/**
 * Where a pallet stands, in the words the pallet page's status card uses
 * (F09 UF-02) — "Coldroom · grid B7", "Loading bay · lane L3",
 * "Dispatched · MIS-0398", "Packing line". ONE phrase for the page and the
 * finder's rows, so the two can never disagree.
 *   · `roomCode` names the room ("Coldroom CR2 · grid B7"). Without it, or when
 *     it does not know the room (rooms not read yet, an inactive room), the
 *     phrase is the page's own room-less one — never a wrong room.
 *   · `missionNumber` is the truck a dispatched pallet left on; null reads
 *     "Dispatched".
 *   · `retipped`: a received pallet stripped into bins is DISPATCHED in the
 *     data but never left on a truck.
 * Null for a state with no place to name (returned, rebinned, cancelled…).
 */
export function palletWhereabouts(r: WhereaboutsRow, opts: Parameters<typeof palletWhereaboutsBase>[1] = {}): string | null {
  const base = palletWhereaboutsBase(r, opts)
  if (base && opts.onTruck && opts.missionNumber && r.status !== 'DISPATCHED' && !opts.retipped) {
    return `${base} · on ${opts.missionNumber}`
  }
  return base
}

// Same status switch as the old palletWhereabouts, renamed when the finder's
// truck suffix was split out; its complexity is unchanged from main.
// fallow-ignore-next-line complexity
function palletWhereaboutsBase(r: WhereaboutsRow, opts: {
  missionNumber?: string | null
  roomCode?:      (roomId: string) => string | null | undefined
  retipped?:      boolean
  /**
   * U15 UF-01 (finder only): name the truck a LOADED pallet is on that has not
   * left — "Loading bay · on M-2026-00412", "Coldroom CR2 · grid B7 · on M-…".
   * Off by default: the pallet page prints the truck as its own link. A
   * DISPATCHED pallet already names it, so this never doubles it.
   */
  onTruck?:       boolean
} = {}): string | null {
  const lane = r.loading_bay_lane ? `Loading bay · lane ${r.loading_bay_lane}` : null
  switch (true) {
    case r.status === 'IN_COLDROOM': {
      const room = r.coldroom_id ? opts.roomCode?.(r.coldroom_id) : null
      const head = room ? `Coldroom ${room}` : 'Coldroom'
      // 'Coldroom (no grid set)' used to be the answer for BOTH an unrecorded
      // cell and a deliberate overflow placement, which reads as a fault
      // either way (src/lib/coldroomPlacement.ts tells the two apart).
      if (r.coldroom_grid)     return `${head} · grid ${r.coldroom_grid}`
      if (r.coldroom_overflow) return `${head} · ${OVERFLOW_LABEL.toLowerCase()} (in the room, not on the grid)`
      return `${head} (no grid set)`
    }
    case r.status === 'IN_LOADING_AREA':
      return lane ?? 'Loading bay (no lane)'
    case r.status === 'INSPECTED_APPROVED' || r.status === 'INSPECTED_REJECTED':
      return lane ?? 'Loading bay'
    case !!opts.retipped:
      return 'All cartons stripped into bins · never loaded on a truck'
    case r.status === 'DISPATCHED':
      return opts.missionNumber ? `Dispatched · ${opts.missionNumber}` : 'Dispatched'
    case r.status === 'IN_PRODUCTION':
      return 'Packing line'
    default:
      return null
  }
}

/** A finder row, as far as choosing what Enter opens needs it. */
export interface FinderPick {
  kind:     'pallet' | 'bin'
  display:  string
  /** Found by an EXACT barcode lane (our ssc_id, or a received pallet's sscc). */
  viaSscc?: boolean
}

/**
 * What Enter in the finder opens without a tap (F09 UF-02): the row that IS
 * what was typed or scanned, tried in the Coldroom find's order — a pallet id,
 * then an SSCC, then a bin number. Null when nothing is exact, or when one
 * SSCC names two pallets (a received sscc has no unique constraint): then the
 * list stays up for the operator to choose.
 */
export function exactFinderPick<T extends FinderPick>(hits: readonly T[], typed: string): T | null {
  const key = typed.trim().toUpperCase()
  if (!key) return null
  const byId = hits.find(h => h.kind === 'pallet' && h.display.toUpperCase() === key)
  if (byId) return byId
  const bySscc = hits.filter(h => h.kind === 'pallet' && h.viaSscc)
  if (bySscc.length === 1) return bySscc[0]
  if (bySscc.length > 1) return null
  return hits.find(h => h.kind === 'bin' && h.display.toUpperCase() === key) ?? null
}

/** An ilike pattern that matches `t` EXACTLY, any case — its %, _ and \ escaped. */
export function exactIlike(t: string): string {
  return t.replace(/[\\%_]/g, c => `\\${c}`)
}

type Read = PromiseLike<{ data: unknown; error: unknown }>
export interface FindReads {
  palletById:   (pattern: string) => Read
  palletBySscc: (sscc: string) => Read
  /** pallets.sscc: a third party's label on a pallet we store (F05 UF-15). */
  palletByClientSscc?: (candidates: string[]) => Read
  binByNumber:  (pattern: string) => Read
}

const first = <R>(data: unknown): R | null =>
  (Array.isArray(data) ? data[0] : data) as R | null ?? null

/** The row a typed or scanned value names, or why there is none. */
export type FoundRow<R> =
  | { kind: 'found'; what: FoundWhat; row: R }
  | { kind: 'none';  text: string }
  | { kind: 'error'; text: string }

/**
 * THE lookup order — pallet id, then a valid SSCC, then (when the caller reads
 * bins) a bin number — returning the row itself. Shared by the Coldroom find
 * (which keeps only the cell) and the Pallets landing's PalletFindField
 * (which says where the pallet stands, F09 UF-03), so the two can never try a
 * value in a different order. Never throws: a failed read is `error`.
 */
export async function findFirstRow<R>(
  raw: string,
  reads: Omit<FindReads, 'binByNumber'> & Partial<Pick<FindReads, 'binByNumber'>>,
): Promise<FoundRow<R>> {
  const text = raw.trim()
  if (!text) return { kind: 'none', text }
  const sscc = normaliseSscc(text)
  const clientCands = ssccMatchCandidates(text)
  const tries: [FoundWhat, () => Read][] = [
    ['pallet', () => reads.palletById(exactIlike(text))],
    ...(isValidSscc(sscc) ? [['pallet', () => reads.palletBySscc(sscc)] as [FoundWhat, () => Read]] : []),
    ...(clientCands.length && reads.palletByClientSscc
      ? [['pallet', () => reads.palletByClientSscc!(clientCands)] as [FoundWhat, () => Read]] : []),
    ...(reads.binByNumber ? [['bin', () => reads.binByNumber!(exactIlike(text))] as [FoundWhat, () => Read]] : []),
  ]
  try {
    for (const [what, read] of tries) {
      const { data, error } = await read()
      if (error) return { kind: 'error', text }
      const row = first<R>(data)
      if (row) return { kind: 'found', what, row }
    }
  } catch {
    return { kind: 'error', text }
  }
  return { kind: 'none', text }
}

/** Resolve a typed or scanned value. Never throws: a failed read is `error`. */
export async function findInColdroom(raw: string, reads: FindReads): Promise<ColdroomHit> {
  const hit = await findFirstRow<PlacedRow>(raw, reads)
  return hit.kind === 'found' ? placementOf(hit.what, hit.row) : hit
}

/** The one line the operator reads after a find. */
export function hitSentence(hit: ColdroomHit, roomCode: (id: string) => string): string {
  switch (hit.kind) {
    case 'cell':      return `${hit.label} is in ${roomCode(hit.roomId)} · cell ${hit.cell}`
    case 'overflow':  return `${hit.label} is in ${roomCode(hit.roomId)}, in the overflow — not on a cell`
    case 'nocell':    return `${hit.label} is in ${roomCode(hit.roomId)} but on no cell. Place it below.`
    case 'awaiting':  return `${hit.label} is awaiting placement`
    case 'elsewhere':
      if (hit.where) return `${hit.label} is not in a coldroom · ${hit.where}`
      return `${hit.label} is not in a coldroom (${hit.status.toLowerCase().replace(/_/g, ' ')})`
    case 'none':      return `No pallet or bin "${hit.text}"`
    case 'error':     return `Couldn't search for "${hit.text}" — check the connection and try again`
  }
}

/**
 * Is what was typed or scanned into Go to (the ⌘K palette) a pallet or bin
 * number? When it is, the palette's first row becomes "Find pallet or bin …"
 * and carries the text into the finder (F09 UF-01). Number-shaped is exactly:
 *   · three or more digits on their own, dashes allowed — "04418",
 *     "2026-04418" (a bare year, "2026", counts and lists that year's pallets);
 *   · a whole or partial `<PREFIX>-<YEAR>-<serial>` id, the shape pallets and
 *     bins share — "KF49-2026-044", "RBN-2026-00012", "KF49-2026";
 *   · an SSCC as a scanner sends it ("]C1…") or as a person copies the printed
 *     line ("(00) 3 6009…" — the one shape allowed spaces, because the printed
 *     line has them).
 * Anything else — a space, another word, a short code — is not: "PACKHOUSE-2",
 * "HP22", "week 30" and "why is 04418 rejected?" keep the palette's own rows.
 */
export function looksLikePalletOrBinNumber(raw: string): boolean {
  const t = raw.trim()
  if (/^\]C[01]\d+$/i.test(t)) return true
  if (/^\(00\)[\d ]+$/.test(t)) return /\d/.test(t.slice(4))
  if (/^\d[\d-]*$/.test(t)) return t.replace(/-/g, '').length >= 3
  return /^[A-Z][A-Z0-9]*-\d{4}(-\d*)?$/i.test(t)
}
